<template>
  <div class="w-100">
    <!-- Tabs -->
    <ul
      id="tab-layout"
      class="flex mb-0 list-none flex-wrap pt-3 flex-row gap-2"
    >
      <li
        v-for="(tab, i) in items"
        id="tab-link"
        :key="i"
        class="text-base m-0 flex-auto text-center w-full md:w-auto font-bold px-5 py-3 rounded-t-md block leading-normal cursor-pointer border-solid border-2 border-b-0"
        :class="isWhiteTabs ? `text-lg ${tabClass(tab.title)}` : `text-base ${tabClass(tab.title)}`"
        @click="toggleTabs(i)"
      >
        <t-icon
          v-if="tab.icon"
          :name="tab.icon"
        />
        {{ tab.title }}
      </li>
    </ul>
    <!-- Tab Content -->
    <div
      class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 rounded-b-md border-solid border-2 border-t-0"
      :class="isBlueTabs ? 'border-none' : isWhiteTabs ? 'border-none' : 'border-primary-140' "
    >
      <div class="px-4 py-5 flex-auto">
        <div class="tab-content tab-space">
          <h6
            v-if="selectedItem.description"
            class="font-bold"
          >
            {{ selectedItem.description }}
          </h6>
          <!-- eslint-disable vue/no-v-html -->
          <!-- it is very bad to use `v-html`, but because we store all that data in the DB as raw html there is no way around it -->
          <slot :name="selectedItem.name">
            <div v-html="selectedItem.text" />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, } from 'pinia';
import { useSearchStore, } from '../../stores/search';

export default {
  name: 'Tabs',
  props: {
    selected: {
      type: Number,
      required: false,
      default: 0,
    },
    items: {
      type: Array,
      required: true,
    },
    isBlueTabs: {
      type: Boolean,
      required: false,
    },
    isWhiteTabs: {
      type: Boolean,
      required: false,
    },
  },
  emits: ['input'],
  data() {
    return {
      selectedTab: this.selected,
      selectedItem: {},
    };
  },
  computed: {
    ...mapState(useSearchStore, ['page']),
    tabClass() {
      if (this.isBlueTabs === true) {
        return title => this.selectedItem.title === title
          ? 'text-text-secondary bg-white border-primary-140'
          : 'text-white bg-background-primary border-primary-140';
      } else if (this.isWhiteTabs) {
        return title => this.selectedItem.title === title
          ? 'text-text-secondary bg-white border-none'
          : 'text-black bg-divider border-none';
      } else {
        return title => this.selectedItem.title === title
          ? 'text-primary-140 bg-white border-primary-140'
          : 'text-white bg-primary-140 border-primary-140';
      }
    },
  },
  watch: {
    // watching the selected item because we dynamically change them on some
    // pages, so this just watches to see if there are any extra changes.
    selected(s) {
      this.setSelected(s);
    },
  },
  mounted() {
    this.setSelected(this.selected);
  },
  methods: {
    ...mapState(useSearchStore, [
      'applyFilters', 'setPage', 'setProfession',
    ]),
    toggleTabs(tabNumber) {
      this.$emit('input', tabNumber);
      if (this.page.startsWith('CORE100') && tabNumber === 1) {
        this.$router.push('/subscription/core100/pt');
        this.setProfession({
          nothing: 2,
          id: 'PT/PTA',
          name: 'Physical Therapist',
          url: 'physical-therapist',
          imagePath: 'https://summitproedu.imgix.net/landing-pages/pt-phippf.jpg',
          paragraph: 'Physical Therapists are challenged with achieving optimal functional outcomes for a wide range of orthopedic and neurological problems that result in physical dysfunction. Summit offers continuing education courses that provide PTs & PTAs with high impact CEs. With In-Person, Live Webinar, and Online options (including 1, 2, and 3 credit Online Video & Text courses) Summit provides the physical therapy continuing education you want.',
        });
        this.applyFilters();
      }
      else if (this.page.startsWith('CORE100') && tabNumber === 2) {
        this.$router.push('/subscription/core100/ot');
        this.setProfession({
          do_not_use: 4,
          id: 'OT/OTA',
          name: 'Occupational Therapist',
          url: 'occupational-therapist',
          imagePath: 'https://summitproedu.imgix.net/landing-pages/occupational-therapist.jpg',
          paragraph: 'Occupational Therapists are charged with the important task of restoring functional ability to facilitate optimal performance of daily tasks and sensory regulation. Summit offers continuing education courses that provide OTs & OTAs with CEs for real world impact. With both Live and Online CEUs, including 1, 2, and 3 credit Online Video & Text courses, Summit provides the occupational therapy continuing education you want.',
        });
        this.applyFilters();
      }
      else if (this.page.startsWith('CORE100') && tabNumber === 3) {
        this.$router.push('/subscription/core100/slp');
        this.setProfession({
          do_not_use: 6,
          id: 'SLP',
          name: 'Speech-Language Pathologist',
          url: 'speech-language-pathologist',
          imagePath: 'https://summitproedu.imgix.net/landing-pages/speech-language-pathologist.jpg',
          paragraph: 'Speech-Language Pathologists address complex, and sometimes invisible dysfunctions related to speaking, feeding, sensory processing, and cognition. Summit offers specialized continuing education courses that provide SLPs with applicable, practical, and relevant CEs. With both Live and Online CE courses, including 1, 2, and 4 hour courses, Summit provides the Speech-Language Pathology continuing education you want.',
        });
        this.applyFilters();
      }
      else if (this.page.startsWith('CORE100') && tabNumber === 0) {
        this.$router.push('/subscription/core100');
      }
    },
    setSelected(tabNumber) {
      this.selectedItem = (tabNumber > this.items.length)
        ? this.items[0]
        : this.items[tabNumber];
    },
  },
};
</script>

<style lang="scss">
// this is needed because the default tailwinds style removed ol list styles by default,
// and html that is stored in the db already has the `ol` and `li` so we are not able to
// easily modify the classes in that html. This is copied from the original site CSS.
.tab-content {
  p {
    margin-bottom: 12px;
  }

  ul {
    margin-bottom: 20px;
    margin-left: 30px;
    list-style: disc;
  }

  ol {
    margin-bottom: 20px;
    margin-left: 30px;
    list-style: outside decimal;
  }

  li {
    margin: 6px 0;
    font-size: 16px;
  }

  h3 {
    border-bottom: 1px solid #ccc;
    margin-bottom: 10px;
    margin-top: 20px;
  }
}
</style>
